import { useState } from 'react';
import { useAffiliateStats } from '@/hooks/useAffiliateStats';
import { getFirebaseServices } from '@/lib/firebase/client';
import { httpsCallable } from 'firebase/functions';
import { Copy, Check, Users, DollarSign, Lock, Unlock, ChevronDown, ChevronUp } from 'lucide-react';
import { Progress } from '@/components/ui/Progress';

export function AffiliateSection() {
  const stats = useAffiliateStats();
  const [copied, setCopied] = useState(false);
  const [referralInput, setReferralInput] = useState('');
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState('');
  const [linkSuccess, setLinkSuccess] = useState(false);
  const [referralsExpanded, setReferralsExpanded] = useState(true);

  const handleCopyLink = async () => {
    if (!stats.referralLink) return;
    
    try {
      await navigator.clipboard.writeText(stats.referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleLinkReferral = async () => {
    if (!referralInput || referralInput.length !== 5) {
      setLinkError('Please enter a valid 5-character code');
      return;
    }

    setLinking(true);
    setLinkError('');
    setLinkSuccess(false);

    try {
      const services = getFirebaseServices();
      if (!services?.functions) throw new Error('Firebase not initialized');

      const linkReferral = httpsCallable(services.functions, 'linkReferral');
      await linkReferral({ code: referralInput.toLowerCase() });

      setLinkSuccess(true);
      setReferralInput('');
      
      // Reload the page to refresh stats
      setTimeout(() => window.location.reload(), 1500);
    } catch (err: any) {
      setLinkError(err.message || 'Failed to link referral code');
    } finally {
      setLinking(false);
    }
  };

  if (stats.loading) {
    return (
      <section className="rounded-2xl border border-white/10 bg-zinc-900/50 p-6">
        <div className="flex items-center gap-3 mb-4">
          <Users className="h-5 w-5 text-purple-400" />
          <h2 className="text-xl font-bold text-white">Affiliate Program</h2>
        </div>
        <p className="text-sm text-white/60">Loading...</p>
      </section>
    );
  }

  return (
    <section className="rounded-2xl border border-white/10 bg-zinc-900/50 p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Users className="h-5 w-5 text-purple-400" />
        <h2 className="text-xl font-bold text-white">Affiliate Program</h2>
      </div>

      {/* Reward Info Box */}
      <div className="p-4 rounded-xl bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-cyan-500/20 border-2 border-purple-400/30">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-lime-400/20 flex items-center justify-center">
            <DollarSign className="h-6 w-6 text-lime-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-white mb-1">Earn $10 Per Friend</h3>
            <p className="text-sm text-white/80 leading-relaxed">
              Share your referral link with friends. When they sign up and earn <span className="font-bold text-lime-300">$50</span>, you automatically get <span className="font-bold text-lime-300">$10</span> added to your balance!
            </p>
            <p className="text-xs text-white/60 mt-2">
              ✓ Unlimited referrals • ✓ No caps • ✓ Withdraw at $50
            </p>
          </div>
        </div>
      </div>

      {/* Affiliate Link - Always show */}
      <div className="space-y-3">
        <div>
          <label className="text-sm font-semibold text-white/80 mb-1 block">
            Your Referral Link
          </label>
          {stats.affiliateCode ? (
            <>
              <div className="flex gap-2">
                <input
                  value={stats.referralLink}
                  readOnly
                  className="flex-1 font-mono text-sm bg-zinc-800 border border-white/10 rounded-2xl px-3 py-2 text-white"
                />
                <button
                  onClick={handleCopyLink}
                  className="border border-purple-400/30 bg-purple-400/10 text-purple-300 hover:bg-purple-400/20 rounded-2xl px-3 py-2 transition"
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-white/50 mt-1">
                Share this link with friends. Earn $10 when they earn $50!
              </p>
            </>
          ) : (
            <div className="p-4 rounded-lg bg-zinc-800/50 border border-white/10">
              <p className="text-sm text-white/70">
                Your affiliate code will be generated automatically. Please refresh if you don't see it.
              </p>
            </div>
          )}
        </div>

        {/* Affiliate Earnings */}
        {stats.totalEarnings > 0 && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-lime-400/10 border border-lime-400/20">
            <DollarSign className="h-5 w-5 text-lime-400" />
            <div>
              <p className="text-sm font-semibold text-white">
                Affiliate Earnings: ${stats.totalEarnings.toFixed(2)}
              </p>
              <p className="text-xs text-white/60">
                Added to your balance • Withdraw at $50
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Add Referral Code (if eligible) */}
      {stats.canAddReferralCode && !stats.isLocked && (
        <div className="space-y-3 p-4 rounded-lg bg-cyan-400/10 border border-cyan-400/20">
          <div className="flex items-center gap-2">
            <Unlock className="h-4 w-4 text-cyan-400" />
            <p className="text-sm font-semibold text-white">
              Have a friend code? Add it now!
            </p>
          </div>
          <p className="text-xs text-white/60">
            {stats.daysRemaining !== undefined && (
              <span className="font-semibold text-cyan-300">{stats.daysRemaining} {stats.daysRemaining === 1 ? 'day' : 'days'} remaining</span>
            )} to link a referral code.
          </p>
          <div className="flex gap-2">
            <input
              value={referralInput}
              onChange={(e) => setReferralInput(e.target.value.toLowerCase())}
              placeholder="Enter 5-char code"
              maxLength={5}
              className="flex-1 bg-zinc-800 border border-white/10 rounded-2xl px-3 py-2 text-white uppercase"
            />
            <button
              onClick={handleLinkReferral}
              disabled={linking || referralInput.length !== 5}
              className="bg-cyan-400 text-zinc-950 hover:bg-cyan-300 rounded-2xl px-4 py-2 font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {linking ? 'Linking...' : 'Link'}
            </button>
          </div>
          {linkError && <p className="text-xs text-red-400">{linkError}</p>}
          {linkSuccess && (
            <p className="text-xs text-lime-400">✓ Successfully linked! Reloading...</p>
          )}
        </div>
      )}

      {/* Locked Referral Status */}
      {stats.isLocked && stats.referredByCode && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-zinc-800/50 border border-white/10">
          <Lock className="h-4 w-4 text-white/50" />
          <p className="text-sm text-white/70">
            Referred by: <span className="font-mono font-semibold text-purple-300">{stats.referredByCode}</span>
          </p>
        </div>
      )}

      {/* Referrals List */}
      {stats.referrals.length > 0 && (
        <div className="space-y-3">
          <button
            onClick={() => setReferralsExpanded(!referralsExpanded)}
            className="w-full flex items-center justify-between text-sm font-semibold text-white/80 hover:text-white transition"
          >
            <span>Your Referrals ({stats.referrals.length})</span>
            {referralsExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>
          {referralsExpanded && (
            <div className="space-y-2">
              {stats.referrals.map((ref) => (
                <div
                  key={ref.uid}
                  className="p-3 rounded-lg bg-zinc-800/50 border border-white/10 space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-white">{ref.handle}</p>
                    <p className="text-xs text-white/60">
                      ${ref.earnings.toFixed(2)} / $50.00
                    </p>
                  </div>
                  <Progress value={ref.progress} className="h-2" />
                  {ref.progress >= 100 ? (
                    <p className="text-xs text-lime-400">✓ Reward earned!</p>
                  ) : (
                    <p className="text-xs text-white/50">
                      ${(50 - ref.earnings).toFixed(2)} until you earn $10
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {stats.referrals.length === 0 && stats.affiliateCode && (
        <div className="text-center py-6">
          <Users className="h-8 w-8 text-white/20 mx-auto mb-2" />
          <p className="text-sm text-white/50">No referrals yet</p>
          <p className="text-xs text-white/30 mt-1">Share your link to get started!</p>
        </div>
      )}
    </section>
  );
}
